import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { OnboardingState } from '../types/onboarding';
import { onboardingService } from '../services/onboardingService';

// Monotonic id for hydrate() calls. The route error boundary is keyed by pathname,
// so the whole tree remounts on every navigation and hydrate() fires again — with
// no ordering guarantee between the responses. Only the highest seq may write.
let hydrateSeq = 0;

interface OnboardingStore {
  state: OnboardingState | null;
  panelMinimized: boolean;
  // Whether the welcome bridge has been dismissed for the current tour. Lives in
  // the store (not component state) because the route error boundary remounts
  // the whole page tree on every navigation — local state would reset and the
  // welcome card would reappear after the user clicks a nav item.
  welcomeSeen: boolean;
  hydrate: () => Promise<void>;
  setState: (s: OnboardingState) => void;
  advance: (completedStep?: number) => Promise<void>;
  skip: () => Promise<void>;
  replay: () => Promise<void>;
  setMinimized: (v: boolean) => void;
  setWelcomeSeen: (v: boolean) => void;
}

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      state: null,
      panelMinimized: false,
      welcomeSeen: false,
      hydrate: async () => {
        const seq = ++hydrateSeq;
        const server = await onboardingService.getState();
        set((prev) => {
          // Several of these GETs can be in flight at once (see hydrateSeq); only
          // the newest one still describes the server. An older response arriving
          // late is stale by definition, whatever it says.
          if (seq !== hydrateSeq) return {};
          const local = prev.state;
          // The route error boundary remounts the whole tree on every navigation,
          // re-hydrating from the server each time. When the user just completed a
          // step, that GET races the advance POST: if the GET read the pre-advance
          // state, it would clobber the store back one step and the tour would look
          // stuck ("do it twice"). Keep the local state when it's exactly one
          // in-progress step ahead — the advance that produced it already committed
          // server-side, so this GET is simply stale.
          if (
            local &&
            local.status === 'in_progress' &&
            server.status === 'in_progress' &&
            server.currentStep === local.currentStep - 1
          ) {
            return {};
          }
          // Don't let a slow GET resurrect the run this client already watched end.
          // hydrate is the only writer that takes the server's word unconditionally
          // — every other path guards on `status === 'in_progress'`, PUT
          // /users/me/onboarding-state refuses a status change outright, and
          // /onboarding/advance and /complete answer 409 once the tour is over — so
          // a response issued before skip/complete committed is the one way a
          // finished tour could flip back to in_progress. The completion modal
          // navigates the instant complete() resolves, which remounts the tree and
          // fires another hydrate right in that window.
          //
          // Matched on startedAt EQUALITY, so this only ever silences a stale read
          // of that exact run: /onboarding/replay stamps a fresh startedAt, and so
          // does a different user signing in (the store is not cleared on logout).
          // Both still get through.
          if (
            local &&
            (local.status === 'completed' || local.status === 'skipped') &&
            server.status === 'in_progress' &&
            !!local.startedAt &&
            server.startedAt === local.startedAt
          ) {
            return {};
          }
          return { state: server };
        });
      },
      setState: (s) => set({ state: s }),
      // Send the step the client believes it just completed so the server can treat
      // a duplicate/late advance (event re-fire or a navigation-hydrate race) as an
      // idempotent no-op instead of a 409. Defaults to the store's current step.
      advance: async (completedStep) => {
        const step = completedStep ?? get().state?.currentStep;
        set({ state: await onboardingService.advance(step) });
      },
      skip: async () => { set({ state: await onboardingService.skip() }); },
      // A replayed tour starts over, so greet the user again.
      replay: async () => { set({ state: await onboardingService.replay(), welcomeSeen: false }); },
      setMinimized: (v) => set({ panelMinimized: v }),
      setWelcomeSeen: (v) => set({ welcomeSeen: v }),
    }),
    { name: 'onboarding-storage', partialize: (s) => ({ panelMinimized: s.panelMinimized }) }
  )
);
